/**
 * rtdbSnapshotTelemetry - Pure helpers for honest RTDB snapshot telemetry
 *
 * `onValue()` re-delivers the *entire* current node on every change, so naively
 * calling `JSON.stringify(rawData).length` on every snapshot both:
 *   (a) overstates real bandwidth — wire traffic after the initial sync is
 *       delta-sized (Firebase only sends the changed data over the wire); and
 *   (b) costs an O(board-size) stringify on every edit, not just the first load.
 *
 * `computeSnapshotTelemetry` records the full stringified size only for the
 * FIRST snapshot of a given listener lifecycle (the initial sync). Every
 * subsequent snapshot for that same listener is a delta event: it is flagged
 * as such and reported with size 0 rather than re-stringifying the whole node.
 *
 * There is no cheap way to measure the true delta size from `onValue()` alone
 * (the SDK does not expose changed-child info the way Firestore's
 * `docChanges()` does) — diffing the previous and next full node would itself
 * require an O(board-size) pass, defeating the purpose. So delta events are
 * honestly reported as "unmeasured" (size 0) rather than guessed at.
 */

export interface RtdbSnapshotTelemetry {
  /** Whether the node currently has data (vs. null/deleted) */
  exists: boolean;
  /**
   * Byte size of the full node, stringified. Only computed for the first
   * snapshot of a listener lifecycle (initialSync === true); 0 otherwise.
   */
  size: number;
  /** True for the first snapshot of a listener lifecycle, false for every delta after it */
  isInitialSync: boolean;
}

/**
 * Compute telemetry fields for one RTDB `onValue` snapshot callback.
 *
 * @param rawData - The raw value from `snapshot.val()`
 * @param isFirstSnapshot - Whether this is the first snapshot seen since the
 *   listener was (re)subscribed. Callers own this bit (a simple closure flag
 *   flipped to true after the first call) since it depends on listener
 *   lifecycle, not on the data itself.
 */
export function computeSnapshotTelemetry(
  rawData: unknown,
  isFirstSnapshot: boolean
): RtdbSnapshotTelemetry {
  const exists = rawData !== null && rawData !== undefined;

  // Full-node stringify is only "honest" bandwidth accounting on the initial
  // sync — that's the one snapshot where the client really did download the
  // whole node. Every later snapshot for the same listener is a delta.
  const size = isFirstSnapshot && exists ? JSON.stringify(rawData).length : 0;

  return { exists, size, isInitialSync: isFirstSnapshot };
}
